import { useState, useRef, useEffect, useCallback } from "react";
import RecapCommande from "../../components/commandeInProgress";
import CommandeEncours from "../../components/commandeEncours";
import CommandeLivre from "../../components/commandeLivre";
import CommandeAnnulé from "../../components/commandeAnnulé";
import CommandePretTrans from "../../components/commandePretTrans";
import CommandeValide from "../../components/commandeValide";
import CommandAchete from "../../components/commandWaiting";
import { useTranslation } from "react-i18next";
import Breadcrumb from "../../components/Breadcrumb";
import {
  Clock,
  ShoppingBag,
  CheckCircle2,
  Truck,
  PackageCheck,
  Home,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { scrollToTop } from "../../lib/utils";

const MyComponent = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [underlineStyle, setUnderlineStyle] = useState({
    width: 0,
    offset: 0,
  });
  const [isMobile, setIsMobile] = useState(false);
  const { t, i18n } = useTranslation();

  const navRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // --- Configuration des onglets ---
  const tabNav = [
    { label: "en_cours", id: 0, icon: Clock, color: "text-emerald-500" },
    { label: "achete", id: 2, icon: ShoppingBag, color: "text-emerald-500" },
    { label: "validees", id: 5, icon: CheckCircle2, color: "text-emerald-500" },
    {
      label: "pretes_transport",
      id: 4,
      icon: PackageCheck,
      color: "text-emerald-500",
    },
    { label: "En transit", id: 1, icon: Truck, color: "text-emerald-500" },
    { label: "livrees", id: 6, icon: Home, color: "text-emerald-500" },
  ];

  const handleNavClick = (index: number) => {
    setActiveIndex(index);
    // Centrer l'onglet cliqué sur mobile
    if (isMobile && scrollRef.current) {
      const el = navRefs.current[index];
      if (el) {
        const container = scrollRef.current;
        const targetScroll =
          el.offsetLeft - container.offsetWidth / 2 + el.offsetWidth / 2;
        container.scrollTo({ left: targetScroll, behavior: "smooth" });
      }
    }
  };

  useEffect(() => {
    scrollToTop()
  }, [])

  // --- Détection mobile ---
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // --- Calcul de la position de l'underline ---
  const updateUnderline = useCallback(() => {
    const currentNavRef = navRefs.current[activeIndex];
    if (currentNavRef) {
      setUnderlineStyle({
        width: currentNavRef.offsetWidth,
        offset: currentNavRef.offsetLeft,
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    // Petit délai pour laisser le DOM se stabiliser après un resize
    const timer = setTimeout(updateUnderline, 50);
    updateUnderline();
    return () => clearTimeout(timer);
  }, [activeIndex, isMobile, updateUnderline, i18n.language]);

  // --- Scroll horizontal avec les flèches (desktop) ---
  const scrollBy = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -200 : 200,
      behavior: "smooth",
    });
  };

  // --- Rendu du contenu actif ---
  const renderActiveContent = () => {
    switch (activeIndex) {
      case 0:
        return <CommandeEncours t={t} i18n={i18n} />;
      case 1:
        return <RecapCommande t={t} />;
      case 2:
        return <CommandAchete t={t} />;
      case 3:
        return <CommandeAnnulé t={t} />;
      case 4:
        return <CommandePretTrans t={t} />;
      case 5:
        return <CommandeValide t={t} />;
      case 6:
        return <CommandeLivre t={t} />;
      default:
        return null;
    }
  };

  const activeTab = tabNav.find((tab) => tab.id === activeIndex);

  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* ============ HERO SECTION ============ */}
      <div className="relative w-full h-[220px] md:h-[280px] overflow-hidden">
        {/* Image de fond */}
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url('/bio.svg')` }}
        />
        {/* Overlay gradient moderne */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/40" />
        {/* Formes décoratives */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />

        {/* Contenu hero */}
        <div className="relative z-10 h-full flex flex-col justify-between px-4 md:px-10 lg:px-16 max-w-7xl mx-auto">
          {/* Breadcrumb en haut */}
          <div className="pt-4">
            <Breadcrumb />
          </div>

          {/* Titre */}
          <div className="pb-6">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 tracking-tight">
              {t("mes_commandes")}
            </h1>
            <p className="text-sm md:text-base text-white/70 max-w-lg">
              Suivez et gérez toutes vos commandes en un seul endroit
            </p>
          </div>
        </div>
      </div>

      {/* ============ NAVIGATION TABS (STICKY) ============ */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto relative">
          {/* Flèche gauche (desktop) */}
          {!isMobile && (
            <button
              type="button"
              onClick={() => scrollBy("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
          )}

          {/* Flèche droite (desktop) */}
          {!isMobile && (
            <button
              type="button"
              onClick={() => scrollBy("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          )}

          {/* Container scrollable */}
          <div
            ref={scrollRef}
            className="overflow-x-auto scrollbar-hide px-4 md:px-12"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <div className="relative inline-flex min-w-full">
              {/* Onglets */}
              <div className="flex gap-1 md:gap-2 relative">
                {tabNav.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeIndex === item.id;
                  return (
                    <button
                      key={item.id}
                      ref={(el) => (navRefs.current[item.id] = el)}
                      type="button"
                      onClick={() => handleNavClick(item.id)}
                      className={`
                        group flex items-center gap-2 px-3 md:px-4 py-3 md:py-4
                        whitespace-nowrap transition-all duration-200
                        ${isActive
                          ? "text-green-600"
                          : "text-gray-500 hover:text-gray-800"
                        }
                      `}
                    >
                      <Icon
                        className={`
                          w-4 h-4 md:w-[18px] md:h-[18px] shrink-0 transition-colors
                          ${isActive
                            ? item.color
                            : "text-gray-400 group-hover:text-gray-600"
                          }
                        `}
                      />
                      <span
                        className={`
                          text-xs md:text-sm font-medium
                          ${isActive ? "font-semibold" : ""}
                        `}
                      >
                        {isMobile
                          ? t(item.label).split(" ")[0] +
                          (t(item.label).includes(" ") ? "…" : "")
                          : t(item.label)}
                      </span>
                    </button>
                  );
                })}

                {/* Underline animé */}
                <div
                  className="absolute bottom-0 h-[3px] rounded-full bg-gradient-to-r from-green-500 to-green-500 transition-all duration-300 ease-out"
                  style={{
                    width: `${underlineStyle.width}px`,
                    transform: `translateX(${underlineStyle.offset}px)`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ CONTENU ACTIF ============ */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-6 md:py-8">
        {/* En-tête de section avec icône + titre */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            {activeTab && (
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                <activeTab.icon className={`w-5 h-5 ${activeTab.color}`} />
              </div>
            )}
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900">
                {activeTab ? t(activeTab.label) : t("commandes")}
              </h2>
              <p className="text-xs md:text-sm text-gray-500">
                {t("commandes")}
              </p>
            </div>
          </div>
        </div>

        {/* Contenu dynamique */}
        <div className="space-y-3">{renderActiveContent()}</div>
      </div>

      {/* Styles utilitaires (scrollbar hide) */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default MyComponent;